import { useEffect, useState } from 'react';
import { loadPlateArt, loadVehicleArt } from './plateArt';
import type { PlateArt, PlateFigureId } from '../../engine/plate/figureArt';
import type { VehicleArt } from '../../engine/plate/vehicleArt';
import type { VehicleKind } from '../../engine/vehicleKind';

interface Loaded<T> {
  key: string;
  art: T | null;
  failed: boolean;
}

/**
 * One plate asset (plan body-map P2, P6): null while it loads (the first plate of it fetches its chunk; later ones
 * share the cached parse). A failed load is logged and reported as `failed`, so the plate can say SCAN UNAVAILABLE
 * instead of drawing an empty frame.
 */
function useLoadedArt<K extends string, T>(key: K, load: (key: K) => Promise<T>): Omit<Loaded<T>, 'key'> {
  const [loaded, setLoaded] = useState<Loaded<T>>({ key, art: null, failed: false });
  useEffect(() => {
    let live = true;
    load(key).then(
      (art) => live && setLoaded({ key, art, failed: false }),
      (err: unknown) => {
        console.error('body plate: could not load the art', key, err);
        if (live) setLoaded({ key, art: null, failed: true });
      },
    );
    return () => {
      live = false;
    };
  }, [key, load]);
  // Art that changed since the last load shows nothing stale while the new one arrives.
  return loaded.key === key ? loaded : { art: null, failed: false };
}

/** A person figure's art. */
export const usePlateArt = (id: PlateFigureId) => useLoadedArt<PlateFigureId, PlateArt>(id, loadPlateArt);

/** A vehicle's art. */
export const useVehicleArt = (kind: VehicleKind) => useLoadedArt<VehicleKind, VehicleArt>(kind, loadVehicleArt);
